import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom'; 
import DocumentList from './parts/DocumentList';
import SearchWidget from './widgets/SearchWidget';
import UploadWidget from './widgets/UploadWidget';
import BulkUploadWidget from './widgets/BulkUploadWidget';
import DocumentDetailWidget from './widgets/DocumentDetailWidget';
import { apiService } from './services/api';
import { useSecurity } from './contexts/SecurityContext';
import { useAuth } from './contexts/AuthContext';
import './style/PageStyle/Documents.css';
import { API_BASE_URL } from './services/api/config'; 

const Documents = () => {
  // --- 1. UI State ---
  const [showSearch, setShowSearch] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false); // Added state for Bulk Upload
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- 2. Data State ---
  const [allDocuments, setAllDocuments] = useState([]);

  // --- 3. Filter State ---
  const [activeFilters, setActiveFilters] = useState({
    query: '',
    organisation: 'all',
    category: 'all',
    format: 'all',
    startDate: '',
    endDate: '',
    expireStartDate: '',
    expireEndDate: '',
    showExpiring: false,
    showArchived: false // Default to hiding archived files
  });

  // --- Security & Auth Hooks ---
  const { secureFetch } = useSecurity();
  const { user } = useAuth(); 

  // --- Handle Incoming Search from Dashboard ---
  const location = useLocation();

  useEffect(() => {
    if (location.state && location.state.filters) {
      setActiveFilters(prev => ({
        ...prev,
        ...location.state.filters
      }));
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // --- 4. API Interaction ---
  const loadDocuments = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await apiService.getDocuments();
      setAllDocuments(data);
    } catch (err) {
      console.error(err);
      setError('Failed to load documents. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  // --- 5. Filtering Logic ---
  const filteredDocuments = useMemo(() => {
    return allDocuments.filter(doc => {
      
      // --- ARCHIVE FILTER LOGIC (FIXED) ---
      
      // Case 1: If "Show Archived" is ACTIVE, we ONLY want to see archived files.
      if (activeFilters.showArchived) {
        if (doc.is_archived !== 1) {
          return false; // Hide active files
        }
      } 
      // Case 2: If "Show Archived" is INACTIVE (Default), we ONLY want to see active files.
      else {
        if (doc.is_archived === 1) {
          return false; // Hide archived files
        }
      }

      // --- Rest of the filters ---
      const docName = doc.name ? doc.name.toLowerCase() : '';
      const query = activeFilters.query ? activeFilters.query.toLowerCase() : '';
      const matchesQuery = docName.includes(query);
      
      const matchesOrg = activeFilters.organisation === 'all' || doc.organisation === activeFilters.organisation;
      const matchesCategory = activeFilters.category === 'all' || doc.category === activeFilters.category;
      const matchesFormat = activeFilters.format === 'all' || doc.format === activeFilters.format;
      
      let matchesDate = true;
      if (activeFilters.startDate || activeFilters.endDate) {
        const docDate = new Date(doc.uploadedDate);
        docDate.setHours(0, 0, 0, 0);

        if (activeFilters.startDate) {
          const start = new Date(activeFilters.startDate);
          start.setHours(0, 0, 0, 0);
          if (docDate < start) matchesDate = false;
        }

        if (activeFilters.endDate) {
          const end = new Date(activeFilters.endDate);
          end.setHours(23, 59, 59, 999);
          if (docDate > end) matchesDate = false;
        }
      }

      let matchesExpiration = true;
      const hasExpirationFilter = activeFilters.showExpiring || activeFilters.expireStartDate || activeFilters.expireEndDate;

      if (hasExpirationFilter) {
        if (!doc.expireDate) {
          matchesExpiration = false;
        } else {
          const expDate = new Date(doc.expireDate);
          expDate.setHours(0, 0, 0, 0);
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          if (activeFilters.showExpiring) {
            const limitDate = new Date(today);
            limitDate.setDate(today.getDate() + 30);
            if (expDate > limitDate) matchesExpiration = false;
          } else {
            if (activeFilters.expireStartDate) {
              const start = new Date(activeFilters.expireStartDate);
              start.setHours(0, 0, 0, 0);
              if (expDate < start) matchesExpiration = false;
            }
            if (activeFilters.expireEndDate) {
              const end = new Date(activeFilters.expireEndDate);
              end.setHours(23, 59, 59, 999);
              if (expDate > end) matchesExpiration = false;
            }
          }
        }
      }

      return matchesQuery && matchesOrg && matchesCategory && matchesFormat && matchesDate && matchesExpiration;
    });
  }, [allDocuments, activeFilters]);

  // --- 6. Handlers ---
  const handleDelete = async (id) => {
    try {
      const response = await secureFetch(`${API_BASE_URL}/documents/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setAllDocuments(prev => prev.filter(doc => doc.id !== id));
        if (selectedDocument && selectedDocument.id === id) {
          setSelectedDocument(null);
        }
      } else {
        alert('Failed to delete document. Server returned error.');
      }
    } catch (err) {
      console.error(err);
      alert('Error deleting document.');
    }
  };

  const handleDocumentClick = (doc) => {
    setSelectedDocument(doc);
  };

  const handleSearchApply = (criteria) => {
    setActiveFilters(prev => ({
      ...prev,
      ...criteria,
      query: criteria.query || prev.query 
    }));
    setShowSearch(false); 
  };

  const handleUploadSuccess = () => {
    loadDocuments();
    setShowUpload(false);
    setShowBulkUpload(false); 
  };

  const clearFilters = () => {
    setActiveFilters({ 
      query: '', 
      organisation: 'all', 
      category: 'all', 
      format: 'all', 
      startDate: '', 
      endDate: '',
      expireStartDate: '',
      expireEndDate: '',
      showExpiring: false,
      showArchived: false 
    });
  };

  const handleQuickSearchChange = (e) => {
    setActiveFilters(prev => ({ ...prev, query: e.target.value }));
  };

  // --- Toggle Archived Handler ---
  const toggleArchived = () => {
    setActiveFilters(prev => ({ ...prev, showArchived: !prev.showArchived }));
  };

  const handleDownload = async (doc) => {
    try {
      const token = localStorage.getItem('token'); 

      const response = await fetch(`${API_BASE_URL}/documents/${doc.id}/download`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.indexOf("application/json") !== -1) {
          const errorData = await response.json();
          alert(`Download failed: ${errorData.message}`);
          return;
      }
      if (contentType && contentType.indexOf("text/html") !== -1) {
          alert("Server Error: The file was not found, and the server returned the homepage instead.");
          return;
      }

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        let fileName = doc.name;
        if (!fileName.toLowerCase().endsWith(`.${doc.format.toLowerCase()}`)) {
             fileName = `${fileName}.${doc.format.toLowerCase()}`;
        }

        link.setAttribute('download', fileName); 
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        console.error("Server returned status:", response.status);
        alert("Failed to download document.");
      }
    } catch (error) {
      console.error("Download failed", error);
      alert("Failed to download document.");
    }
  };

  const hasActiveFilters = activeFilters.query || 
                           activeFilters.organisation !== 'all' || 
                           activeFilters.category !== 'all' || 
                           activeFilters.format !== 'all' || 
                           activeFilters.startDate || 
                           activeFilters.endDate ||
                           activeFilters.showExpiring ||
                           activeFilters.expireStartDate ||
                           activeFilters.expireEndDate ||
                           activeFilters.showArchived; 

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Documents</h1>
          <p className="page-subtitle">Manage and organise your files</p>
        </div>
        
        <div className="page-actions">
          <div className="quick-search-wrapper">
            <span className="search-icon">🔎</span>
            <input 
              type="text" 
              className="quick-search-input"
              placeholder="Search File Name..."
              value={activeFilters.query}
              onChange={handleQuickSearchChange}
            />
          </div>

          <button className="btn-secondary" onClick={() => setShowSearch(true)}>
            Advanced Filters
          </button>
          
          {/* Toggle Archived Button */}
          <button 
            className={`btn-secondary ${activeFilters.showArchived ? 'active' : ''}`} 
            onClick={toggleArchived}
            style={{ 
              backgroundColor: activeFilters.showArchived ? '#fff7ed' : 'white', // Light orange bg when active
              borderColor: activeFilters.showArchived ? '#f97316' : '#fdba74',   // Orange border
              color: activeFilters.showArchived ? '#c2410c' : '#ea580c',         // Dark orange text
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            title="Toggle Archived Documents"
          >
            <span>{activeFilters.showArchived ? '📂' : '📁'}</span>
            {activeFilters.showArchived ? 'Hide Archived' : 'Show Archived'}
          </button>

          {/* Bulk Upload Button */}
          <button className="btn-secondary" onClick={() => setShowBulkUpload(true)}>
            Bulk Upload
          </button>

          <button className="btn-primary" onClick={() => setShowUpload(true)}>
            + Upload New
          </button>
        </div>
      </div>

      {hasActiveFilters && (
        <div className="active-filters-banner">
          <div className="active-filters-list">
            <strong>Active Filters:</strong>
            {activeFilters.query && <span className="filter-tag">Search: "{activeFilters.query}"</span>}
            {activeFilters.organisation !== 'all' && <span className="filter-tag">Org: {activeFilters.organisation}</span>}
            {activeFilters.category !== 'all' && <span className="filter-tag">Cat: {activeFilters.category}</span>}
            {activeFilters.format !== 'all' && <span className="filter-tag">Fmt: {activeFilters.format}</span>}
            
            {(activeFilters.startDate || activeFilters.endDate) && (
              <span className="filter-tag">Uploaded: {activeFilters.startDate || '...'} to {activeFilters.endDate || '...'}</span>
            )}

            {activeFilters.showExpiring && (
              <span className="filter-tag" style={{backgroundColor: '#fee2e2', color: '#b91c1c', borderColor: '#fecaca'}}>
                ⚠️ Expiring Soon / Expired
              </span>
            )}
            {!activeFilters.showExpiring && (activeFilters.expireStartDate || activeFilters.expireEndDate) && (
              <span className="filter-tag">Expires: {activeFilters.expireStartDate || '...'} to {activeFilters.expireEndDate || '...'}</span>
            )}
            {/* Archived Tag */}
            {activeFilters.showArchived && (
              <span className="filter-tag" style={{backgroundColor: '#f3f4f6', color: '#4b5563', borderColor: '#d1d5db'}}>
                🗂️ Showing Archived
              </span>
            )}
          </div>
          <button className="btn-clear-filters" onClick={clearFilters}>Clear All</button>
        </div>
      )}

      {isLoading ? (
        <div className="loading-state">Loading documents...</div>
      ) : error ? (
        <div className="error-state">{error}</div>
      ) : (
        <DocumentList 
          documents={filteredDocuments} 
          onDelete={handleDelete} 
          onRowClick={handleDocumentClick} 
          onDownload={handleDownload}
          currentUser={user}
          currentUserRole={user?.role} 
        />
      )}

      {showSearch && (
        <SearchWidget 
          onClose={() => setShowSearch(false)} 
          onSearch={handleSearchApply} 
        />
      )}

      {showUpload && (
        <UploadWidget 
          onClose={() => setShowUpload(false)} 
          onRefresh={handleUploadSuccess} 
          secureFetch={secureFetch}
        />
      )}

      {/* Bulk Upload Widget */}
      {showBulkUpload && (
        <BulkUploadWidget 
          onClose={() => setShowBulkUpload(false)} 
          onRefresh={handleUploadSuccess} 
          secureFetch={secureFetch} 
        />
      )}

      {selectedDocument && (
        <DocumentDetailWidget 
          document={selectedDocument} 
          onClose={() => setSelectedDocument(null)}
          onRefresh={loadDocuments} 
        />
      )}
    </div>
  );
};

export default Documents;
